import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import tw from "twin.macro"
import { Filter } from "../../../clients/FiltersClient.d"
import {
  clearFiltersModal,
  closeFiltersModal,
} from "../../../contexts/Filters/actions"
import { FiltersContext } from "../../../contexts/Filters/context"
import { setQueryFromFilters } from "../../../contexts/Inventory/actions"
import { InventoryContext } from "../../../contexts/Inventory/context"
import { LanguageContext } from "../../../contexts/Language"
import { LocationContext } from "../../../contexts/Location"
import { useTealiumContext } from "../../../contexts/Tealium"
import { Modal } from "../../atoms/Modal"
import { AccessoryFilter } from "../../molecules/AccessoryFilter"
import CheckboxFilter from "../../molecules/CheckboxFilter/CheckboxFilter"
import ColorFilter from "../../molecules/ColorFilter/ColorFilter"
import SwitchFilter from "../../molecules/SwitchFilter/SwitchFilter"
import StaticFilters from "./StaticFilters"
import { Loader } from "../../atoms/Icon"
import DynamicFilters from "./DynamicFilters"
import FilterButtons from "./FilterButtons"
import FilterModalResultsCta from "./FilterModalResultsCta"

const Filters: React.FC<{
  filters: Filter[]
  isLoading: boolean
  modalOpen: boolean
  closeModal: any
}> = ({ filters, modalOpen, closeModal }) => {
  // Define filter types map outside of component body to ensure consistent hook calls
  const filterTypes = useMemo(
    () =>
      new Map<string, React.FC<any>>([
        ["CheckBox", CheckboxFilter],
        ["Switch", SwitchFilter],
        ["ColorSelector", ColorFilter],
        ["AccessoryCheckbox", AccessoryFilter],
      ]),
    [],
  )
  const [{ dealers }] = useContext(LocationContext)
  const [state, dispatch] = useContext(InventoryContext)
  const [filtersState, dispatchFilters] = useContext(FiltersContext)
  const { _ } = useContext(LanguageContext)
  const {
    setRefineInAction,
    setInventoryResults,
    inventoryFilters,
    clearAllInventoryFilterQueue,
  } = useTealiumContext()

  const containerRef = useRef<HTMLDivElement | null>(null)
  const [swipeScrollMessageVisible, setSwipeScrollMessageVisible] =
    useState(true)

  const checkIfScrolledToBottom = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current

      const isScrollingRequired = scrollHeight > clientHeight

      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20 // Allow for rounding errors

      setSwipeScrollMessageVisible(isScrollingRequired && !isAtBottom) // Show message only if scrolling is required and not at the bottom

      // If no scrolling is required, ensure the message is hidden
      if (!isScrollingRequired) {
        setSwipeScrollMessageVisible(false)
      }
    }
  }

  useEffect(() => {
    const container = containerRef.current

    if (container) {
      const handleScroll = () => checkIfScrolledToBottom()

      container.addEventListener("scroll", handleScroll)

      checkIfScrolledToBottom()

      const resizeObserver = new ResizeObserver(() => {
        checkIfScrolledToBottom() // Recheck scroll position when content height changes
      })
      resizeObserver.observe(container)

      return () => {
        container.removeEventListener("scroll", handleScroll)
        resizeObserver.disconnect()
      }
    }
  }, [])

  const yearFilter = filters?.find(yearFilt => yearFilt.name === "YEAR")

  // Memoize sorting filters to avoid unnecessary re-renders
  const sortedFilter = useMemo(
    () =>
      filters?.reduce(
        (
          allFilters: { staticFilters: Filter[]; remainingFilters: Filter[] },
          filter: Filter,
        ) => {
          // Skip YEAR filter if it should be hidden
          if (filter.name === "YEAR") {
            return allFilters
          }
          allFilters[
            filter.name == "PRICE" || filter.name == "AVAILABILITY"
              ? "staticFilters"
              : "remainingFilters"
          ]?.push(filter)
          return allFilters
        },
        { staticFilters: [], remainingFilters: [] },
      ),
    [filters],
  )

  return (
    <Modal
      open={modalOpen}
      onClose={closeModal}
      closeStyled={true}
      closeButtonStyles={tw`h-[4rem] md:(absolute bottom-auto top-0 left-0 right-0 rounded-tr-2xl rounded-tl-2xl)`}
      css={[
        tw`pt-0 min-h-[fit-content] max-h-full top-0 bottom-0 relative`,
        tw`md:(max-w-[90%] rounded-[20px])`,
        tw`landscape:(max-w-[100%])`, // 👈 Apply only on mobile-landscape
        tw`xl:(max-w-[900px])`,
      ]}
    >
      <div
        css={[
          tw`relative overflow-y-scroll overflow-x-hidden h-[calc(96dvh - 1.25rem)] pt-[4rem] pb-20 grid grid-cols-1 px-4`,
          tw`md:(h-[calc(60dvh - 1.25rem)] px-8)`,
          tw`landscape:h-[96dvh] landscape:pb-10`, // 👈 Apply only on mobile-landscape
          tw`xl:(grid-cols-2 gap-0 mb-10 h-[calc(80vh - 1.25rem)])`,
        ]}
        ref={containerRef}
        className="optimizelyInventoryFilters"
      >
        <FilterButtons
          filters={filters}
          filtersState={filtersState}
          dispatchFilters={dispatchFilters}
          clearAllInventoryFilterQueue={clearAllInventoryFilterQueue}
          clearFiltersModal={clearFiltersModal}
        />

        <div css={[tw`col-span-2 mt-[124px]`]}>
          <StaticFilters
            sortedFilters={sortedFilter?.staticFilters}
            filters={filters}
            clearAllInventoryFilterQueue={clearAllInventoryFilterQueue}
            clearFiltersModal={clearFiltersModal}
            dispatchFilters={dispatchFilters}
          />

          {sortedFilter?.remainingFilters ? (
            sortedFilter?.remainingFilters.map((filter: Filter, index) => (
              <DynamicFilters
                key={`${filter.name}-${index}`}
                filter={filter}
                index={index}
                modalOpen={modalOpen}
                filterTypes={filterTypes}
                hideYearFilter={
                  (yearFilter && yearFilter.values?.length < 2) || false
                }
              />
            ))
          ) : (
            <Loader
              color="red-400"
              css={[tw`max-h-20 animate-spin mt-24 mb-28 mx-auto`]}
            />
          )}
        </div>
      </div>

      <FilterModalResultsCta
        state={state}
        dealers={dealers}
        filtersState={filtersState}
        dispatchFilters={dispatchFilters}
        closeFiltersModal={closeFiltersModal}
        setInventoryResults={setInventoryResults}
        setRefineInAction={setRefineInAction}
        inventoryFilters={inventoryFilters}
        sortedFilter={sortedFilter}
        setQueryFromFilters={setQueryFromFilters}
        dispatch={dispatch}
        swipeScrollMessageVisible={swipeScrollMessageVisible}
      />
    </Modal>
  )
}

export default Filters
